'use client'

import React, {
  createContext,
  useContext,
  useMemo,
  useReducer,
  useCallback,
  useEffect,
  useRef
} from 'react'

import { useCountdown } from './hooks/useCountdown'
import { flowReducer } from './reducer'
import { FlowState, Step } from './types'


type Ctx = {
  state: FlowState

  goNext: () => void
  goPrev: () => void
  goIndex: (i: number) => void
  loseLife: () => void
  complete: () => void
  reset: () => void

  enableContinue: () => void
  verifyChallenge: (args: { correct: boolean; feedback?: string }) => void
  retryChallenge: () => void

  interactionDisabled: boolean
  continueDisabled: boolean
}

interface Props {
  /** When this changes (e.g. another level), flow fully resets. Same key + new steps syncs without losing position. */
  flowKey: string
  steps: Step[]
  initialLives?: number
  initialSeconds?: number
  children: React.ReactNode
}

const FlowContext = createContext<Ctx | null>(null)

export const useFlow = () => {
  const ctx = useContext(FlowContext)
  if (!ctx) throw new Error('useFlow must be used within FlowProvider')
  return ctx
}

export function FlowProvider({
  flowKey,
  steps,
  initialLives = 5,
  initialSeconds = 120,
  children
}: Props) {
  const [state, dispatch] = useReducer(flowReducer, {
    steps: [],
    currentIndex: 0,
    status: 'active',
    lives: initialLives,
    maxLives: initialLives,
    timeLeft: initialSeconds,
    canVerify: true,
    canContinue: false,
    phase: 'READY',
    feedback: null
  })

  const lastFlowKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (lastFlowKeyRef.current !== flowKey) {
      lastFlowKeyRef.current = flowKey
      dispatch({
        type: 'INIT',
        steps,
        lives: initialLives,
        timeLeft: initialSeconds
      })
      return
    }
    dispatch({ type: 'SYNC_STEPS', steps })
  }, [flowKey, steps, initialLives, initialSeconds])

  const currentStep = state.steps[state.currentIndex]
  useEffect(() => {
    if (!currentStep) return
    if (currentStep.kind === 'CHALLENGE') {
      const secs = Number(currentStep.time_limit) || 0
      dispatch({ type: 'START_CHALLENGE', timeLeft: secs })
    } else {
      dispatch({ type: 'CLEAR_FEEDBACK' })
    }
  }, [currentStep?.id])

  const hasTimer =
    currentStep?.kind === 'CHALLENGE' && currentStep?.time_limit > 0
  const running = state.status === 'active' && hasTimer

  const onTick = useCallback((s: number) => {
    dispatch({ type: 'SET_TIME_LEFT', value: s } as any)
  }, [])
  const onEnd = useCallback(() => dispatch({ type: 'TIME_OVER' }), [])

  useCountdown({ running, seconds: state.timeLeft, onTick, onEnd })

  const value = useMemo<Ctx>(
    () => ({
      state,
      goNext: () => dispatch({ type: 'GO_NEXT' }),
      goPrev: () => dispatch({ type: 'GO_PREV' }),
      goIndex: (i: number) => dispatch({ type: 'GO_INDEX', index: i }),
      loseLife: () => dispatch({ type: 'LOSE_LIFE' }),
      complete: () => dispatch({ type: 'COMPLETE' }),
      reset: () => dispatch({ type: 'RESET' }),

      enableContinue: () => dispatch({ type: 'ENABLE_CONTINUE' }),
      verifyChallenge: ({ correct, feedback }) =>
        dispatch({ type: 'VERIFY_RESULT', correct, feedback }),
      retryChallenge: () => dispatch({ type: 'RETRY' }),

      interactionDisabled: state.phase !== 'READY',
      continueDisabled: !state.canContinue
    }),
    [state]
  )

  return <FlowContext.Provider value={value}>{children}</FlowContext.Provider>
}
